import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Autocomplete, Grid, TextField } from '@mui/material';
import api from '@/lib/api';
import { PickerItem } from '@/@types/controls/controls';
import { DCRecord } from '@/@types/lib/dataController';
import { PickerItemValue } from '@/@types/models/model';

type IPublicIzletiFiltersProps = {
  onChangeFilter: (source: string, value: PickerItemValue[] | PickerItemValue | null) => void;
};

const timeFramePickerItems: PickerItem[] = [
  {
    label: 'timeframe.month.1',
    value: 1,
  },
  {
    label: 'timeframe.month.3',
    value: 2,
  },
  {
    label: 'timeframe.month.6',
    value: 3,
  },
];

const excursionTypePickerItems: PickerItem[] = [
  {
    label: 'excursion.type.one_day',
    value: 1,
  },
  {
    label: 'excursion.type.multiple_days',
    value: 2,
  },
];

const STORAGE_KEY = 'public_izleti_filter_state';
type FilterState = Record<string, PickerItemValue[]>;
const initialFilterState: FilterState = {};

const PublicIzletiFilters = (props: IPublicIzletiFiltersProps) => {
  const { t } = useTranslation();
  const apiInstance = new api();

  const { onChangeFilter } = props;

  const [[lokacijePickerItems, organizatoriPickerItems, vodiciTypePickerItems], setPickerItems] = useState<
    [PickerItem[], PickerItem[], PickerItem[]]
  >([[], [], []]);

  useEffect(() => {
    fetchPickerData();
  }, []);

  const fetchPickerData = () => {
    apiInstance
      ._get(`${process.env.REACT_APP_APIPATH}public/izleti-filters`)
      .then((resp) => {
        if (resp.success) {
          if (resp.data && (resp.data as DCRecord).lokacije && (resp.data as DCRecord).organizatori) {
            setPickerItems([
              (resp.data as DCRecord).lokacije as PickerItem[],
              (resp.data as DCRecord).organizatori as PickerItem[],
              (resp.data as DCRecord).vodici as PickerItem[],
            ]);
          } else {
            // should we notify user about no data found?
          }
        } else {
          // TODO: SNACKBAR?
        }
      })
      .catch((resp) => {
        console.log('error');
        // TODO: SNACKBAR?
      });
  };

  const filters = useMemo(() => {
    return [
      {
        code: 'lok',
        ttoken: 'izleti.filters.lokacije',
        options: lokacijePickerItems,
        multi: true,
      },
      {
        code: 'vrem_okvir',
        ttoken: 'izleti.filters.vremenski_okvir',
        options: timeFramePickerItems,
        multi: true,
      },
      {
        code: 'org',
        ttoken: 'izleti.filters.organizator',
        options: organizatoriPickerItems,
        multi: true,
      },
      {
        code: 'tip',
        ttoken: 'izleti.filters.tip_izleta',
        options: excursionTypePickerItems,
        multi: true,
      },
      {
        code: 'vod',
        ttoken: 'izleti.filters.vodici',
        options: vodiciTypePickerItems,
        multi: true,
      },
    ];
  }, [lokacijePickerItems, organizatoriPickerItems, excursionTypePickerItems, timeFramePickerItems, vodiciTypePickerItems]);

  const [selectedFilters, setSelectedFilters] = useState<FilterState>(initialFilterState);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed === 'object' && parsed !== null) {
          setSelectedFilters(parsed);
        }
      }
    } catch (err) {
      console.warn('Failed to parse public izleti filters from localStorage:', err);
      setSelectedFilters(initialFilterState);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedFilters));
    } catch (err) {
      console.warn('Failed to save public izleti filters to localStorage:', err);
    }
  }, [selectedFilters]);

  useEffect(() => {
    Object.keys(selectedFilters).forEach((filterKey) => {
      onChangeFilter(filterKey, selectedFilters[filterKey].length > 0 ? selectedFilters[filterKey] : null);
    });
  }, [selectedFilters]);

  return (
    <Grid container spacing={2}>
      {filters.map((filter) => {
        return (
          <Grid key={`${filter.code}-filter-grid`} size={{xs: 6, lg: 2}} display={'flex'} flexDirection={'column'} gap={2}>
            {/* <Typography color={"primary"}>{t(filter.ttoken)}</Typography>
                        <Divider sx={{borderColor: "primary.main"}}/>
                        <Stack gap={1}>
                            <Chip sx={{width: "fit-content"}} label="Test 1" onDelete={onFilterDelete} color='success'/>
                            <Chip sx={{width: "fit-content"}} label="Test 2 asdasd" onDelete={onFilterDelete} color='success'/>
                        </Stack> */}
            {
              <Autocomplete
                filterSelectedOptions
                multiple={filter.multi}
                options={filter.options}
                getOptionLabel={(option) => t(option.label)}
                key={'filter-' + filter.code}
                id={'filter-' + filter.code}
                onChange={(evt, val) => {
                  const values = val ? (Array.isArray(val) ? val.map((v) => v.value) : [val.value]) : [];
                  setSelectedFilters((prev) => ({ ...prev, [filter.code]: values }));
                }}
                value={filter.options.filter((opt) => selectedFilters[filter.code]?.includes(opt.value))}
                renderInput={(params) => <TextField {...params} label={t(filter.ttoken)} variant="standard" />}
                ChipProps={{ color: 'success', size: 'small' }}
              />
            }
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PublicIzletiFilters;
